import type { ReactNode } from "react";

export type CircleTone = "neutral" | "danger" | "active" | "primary";

interface CircleBtnProps {
  tone: CircleTone;
  onClick: () => void;
  label: string;
  icon: ReactNode;
  ariaPressed?: boolean;
  disabled?: boolean;
  /** Override the default 11 (44px) size — used by the floating widget,
   *  which wants slightly smaller controls in the pill. */
  size?: "sm" | "md";
  /** Optional numeric badge rendered top-right (chat unread, people count). */
  badge?: number;
}

/**
 * Canonical small round button used by MeetingControls and FloatingCallWidget
 * for mic / camera / end-call style toggles. Three tonal variants:
 *   - neutral: rest state, surface bg with border
 *   - danger : red bg, white icon (off / end-call)
 *   - active : accent tint (e.g., reactions popover open)
 */
export function CircleBtn({
  tone,
  onClick,
  label,
  icon,
  ariaPressed,
  disabled,
  size = "md",
  badge,
}: CircleBtnProps) {
  const toneClass =
    tone === "danger"
      ? "bg-red-500 text-white"
      : tone === "primary"
        ? "bg-primary text-primary-foreground"
      : tone === "active"
        ? "bg-primary/15 text-primary"
        : "bg-foreground/[0.06] border border-border text-foreground";
  const sizeClass = size === "sm" ? "size-9" : "size-11";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      aria-pressed={ariaPressed ?? tone !== "neutral"}
      className={`relative ${sizeClass} ${toneClass} rounded-full flex items-center justify-center transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100`}
    >
      {icon}
      {badge !== undefined && badge > 0 && (
        <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full text-[10px] font-bold flex items-center justify-center bg-primary text-primary-foreground">
          {badge > 9 ? "9+" : badge}
        </span>
      )}
    </button>
  );
}
